'use client';

import { useState, useMemo } from 'react';
import { X, ChevronDown } from 'lucide-react';
import { HttpTypes } from '@medusajs/types';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import CountrySelect from '@modules/layout/components/country-select';
import { useToggleState } from '@medusajs/ui';

interface JinMobileMenuProps {
  regions: HttpTypes.StoreRegion[] | null;
  collections: HttpTypes.StoreCollection[];
  categories: HttpTypes.StoreProductCategory[];
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Build mobile nav items dynamically from categories.
 * Root categories with children become expandable sections.
 * Root categories without children become simple links (if nav_visible).
 */
function buildMobileNavCategories(categories: HttpTypes.StoreProductCategory[]) {
  const roots = categories.filter(c => !c.parent_category_id);
  const expandable: { parent: HttpTypes.StoreProductCategory; children: HttpTypes.StoreProductCategory[] }[] = [];
  const simpleLinks: HttpTypes.StoreProductCategory[] = [];

  for (const cat of roots) {
    const children = (cat.category_children || []) as HttpTypes.StoreProductCategory[];
    if (children.length === 0) {
      if ((cat as any).metadata?.nav_visible) {
        simpleLinks.push(cat);
      }
      continue;
    }
    expandable.push({ parent: cat, children });
  }

  return { expandable, simpleLinks };
}

export function JinMobileMenu({ regions, collections, categories, isOpen, onClose }: JinMobileMenuProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [expandedSubSection, setExpandedSubSection] = useState<string | null>(null);
  const toggleState = useToggleState();

  const { expandable, simpleLinks } = useMemo(() => buildMobileNavCategories(categories), [categories]);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
    setExpandedSubSection(null);
  };

  const toggleSubSection = (section: string) => {
    setExpandedSubSection(expandedSubSection === section ? null : section);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        onClick={onClose}
      />
      
      {/* Menu Panel */}
      <div className="fixed top-0 left-0 h-full w-[85%] max-w-sm bg-white z-50 shadow-2xl overflow-y-auto lg:hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-bold tracking-wider uppercase">Menu</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="p-6">
          <ul className="space-y-1">
            <li>
              <LocalizedClientLink 
                href="/" 
                className="block py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                onClick={onClose}
              >
                Home
              </LocalizedClientLink>
            </li>
            
            <li>
              <LocalizedClientLink 
                href="/store"
                className="block py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                onClick={onClose}
              >
                New Arrivals
              </LocalizedClientLink>
            </li>

            {/* Dynamic expandable category sections */}
            {expandable.map((section) => (
              <li key={section.parent.id}>
                <button
                  onClick={() => toggleSection(section.parent.id)}
                  className="flex items-center justify-between w-full py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                >
                  {section.parent.name}
                  <ChevronDown 
                    size={16} 
                    className={`transition-transform ${expandedSection === section.parent.id ? 'rotate-180' : ''}`}
                  />
                </button>
                {expandedSection === section.parent.id && (
                  <ul className="pl-4 mt-2 space-y-1 border-l-2 border-gray-200">
                    <li>
                      <LocalizedClientLink 
                        href={`/categories/${section.parent.handle}`}
                        className="block py-2 text-sm font-semibold text-[var(--primary)] hover:underline transition-colors"
                        onClick={onClose}
                      >
                        All {section.parent.name}
                      </LocalizedClientLink>
                    </li>
                    {section.children.map((child: any) => (
                      <li key={child.id}>
                        {child.category_children && child.category_children.length > 0 ? (
                          <>
                            <button
                              onClick={() => toggleSubSection(child.id)}
                              className="flex items-center justify-between w-full py-2 text-sm text-gray-600 font-semibold hover:text-[var(--primary)] transition-colors"
                            >
                              {child.name}
                              <ChevronDown 
                                size={14} 
                                className={`transition-transform ${expandedSubSection === child.id ? 'rotate-180' : ''}`}
                              />
                            </button>
                            {expandedSubSection === child.id && (
                              <ul className="ml-3 mt-1 space-y-1 border-l border-gray-100">
                                <li>
                                  <LocalizedClientLink 
                                    href={`/categories/${child.handle}`}
                                    className="block py-1 pl-3 text-xs text-gray-500 hover:text-[var(--primary)] transition-colors"
                                    onClick={onClose}
                                  >
                                    All {child.name}
                                  </LocalizedClientLink>
                                </li>
                                {child.category_children.map((grandchild: any) => (
                                  <li key={grandchild.id}>
                                    <LocalizedClientLink 
                                      href={`/categories/${grandchild.handle}`}
                                      className="block py-1 pl-3 text-xs text-gray-500 hover:text-[var(--primary)] transition-colors"
                                      onClick={onClose}
                                    >
                                      {grandchild.name}
                                    </LocalizedClientLink>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </>
                        ) : (
                          <LocalizedClientLink 
                            href={`/categories/${child.handle}`}
                            className="block py-2 text-sm text-gray-600 hover:text-[var(--primary)] transition-colors"
                            onClick={onClose}
                          >
                            {child.name}
                          </LocalizedClientLink>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}

            {/* Simple category links */}
            {simpleLinks.map((cat) => (
              <li key={cat.id}>
                <LocalizedClientLink 
                  href={`/categories/${cat.handle}`}
                  className="block py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                  onClick={onClose}
                >
                  {cat.name}
                </LocalizedClientLink>
              </li>
            ))}

            <li>
              <LocalizedClientLink 
                href="/store" 
                className="block py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                onClick={onClose}
              >
                Shop All
              </LocalizedClientLink>
            </li>

            <li>
              <LocalizedClientLink 
                href="/search" 
                className="block py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                onClick={onClose}
              >
                Search
              </LocalizedClientLink>
            </li>

            <li>
              <LocalizedClientLink 
                href="/account" 
                className="block py-3 text-sm font-bold tracking-widest uppercase hover:text-[var(--primary)] transition-colors"
                onClick={onClose}
              >
                Account
              </LocalizedClientLink>
            </li>
          </ul>
        </nav>

        {/* Country/Region Selector */}
        {regions && (
          <div className="p-6 border-t border-gray-200">
            <CountrySelect toggleState={toggleState} regions={regions} />
          </div>
        )}
      </div>
    </>
  );
}
